<template>
    <VJoyFunnel
        v-if="isFunnelVisible"
        v-bind="{}"
        class="profile-edition-resume-import"
        data-testid="profile-edition-experience-resume-import-funnel"
        layout-width="medium"
    >
        <template #funnel-header>
            <VJoyFunnelHeader>
                <template #funnel-header-left>
                    <MaltImg src="/navbar/malt-logo-red.svg" alt="Malt" width="130px" />
                </template>
                <template #funnel-header-right>
                    <VJoyIconButton
                        data-testid="profile-edition-experience-resume-import-funnel-cta-close"
                        :label="t('general.close')"
                        icon="cross"
                        size="small"
                        variant="ghost"
                        @click="handleClickOnFunnelCancel"
                    />
                </template>
            </VJoyFunnelHeader>
        </template>
        <template #funnel-stepper>
            <VJoyStepper
                v-bind="{step: stepperCurrentIndex, steps: stepperStepsCount + 1}"
                data-testid="profile-edition-experience-resume-import-funnel-stepper"
            >
                <VJoyStep
                    v-for="funnelStep in funnelOrderedSteps"
                    :key="`resume-import-step--${funnelStep.name}`"
                    :data-testid="`profile-edition-experience-resume-import-funnel-stepper-step--${funnelStep.name}`"
                >
                    {{ funnelStep.label }}
                </VJoyStep>
            </VJoyStepper>
        </template>
        <template #default>
            <VJoyTemplate>
                <template v-if="title" #template-title>
                    <div class="profile-edition-resume-import__title-wrapper">
                        <h1 class="profile-edition-resume-import__title">{{ title }}</h1>
                    </div>
                </template>
                <template v-if="subtitle" #template-subtitle>{{ subtitle }}</template>
                <template #template-main>
                    <Component
                        :is="currentStepComponent"
                        v-if="!hasError"
                        :entry-point="entryPoint"
                        @entry-point:reset="resetEntryPoint"
                        @funnel-step:skip-step="handleFunnelStepSkip"
                    />
                    <Component
                        :is="currentStepErrorComponent"
                        v-else
                        @funnel-step:retry="handleFunnelStepRetry"
                        @funnel-step:manual-mode="handleFunnelStepRetryManualMode"
                    />
                    <VJoyFunnelFooter>
                        <template #footer-left>
                            <VJoyButton
                                data-testid="profile-edition-experience-resume-import-cta-cancel"
                                :label="t('generic.form.cancel.label')"
                                variant="secondary"
                                :loading="isFunnelCtaCancelLoading"
                                @click="handleClickOnFunnelCancel"
                            />
                        </template>
                        <template #footer-right>
                            <VJoyButton
                                data-testid="profile-edition-experience-resume-import-cta-next"
                                :label="ctaNextLabel"
                                variant="main"
                                :disabled="!isFunnelCtaNextEnabled || hasError"
                                :loading="isFunnelCtaNextLoading"
                                @click="() => handleClickOnFunnelNext(false)"
                            />
                        </template>
                    </VJoyFunnelFooter>
                </template>
            </VJoyTemplate>
        </template>
    </VJoyFunnel>
</template>
<script setup lang="ts">
    import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {navigateTo, useTranslation} from '#imports';
    import {useDisplayMobile} from '@malt/utils';
    import {
        pushVJoySnackbar,
        VJoyButton,
        VJoyIconButton,
        VJoyFunnelFooter,
        VJoyFunnel,
        VJoyFunnelHeader,
        VJoyStep,
        VJoyStepper,
        VJoyTemplate,
    } from '@maltjoy/core-vue';
    import {useExperienceImportHotjarSurvey} from '~/composables/useExperienceImportHotjarSurvey';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useFunnelSteps} from '../composable/useFunnelSteps';
    import {useResumeImportStore} from '../store/useResumeImportStore';
    import {ResumeImportErrorWithRedirection} from '../types/ResumeImportError';
    import {eventBus} from '@/config/events';

    const {t} = useTranslation();
    const {triggerExperienceHotjarSurveyEvent} = useExperienceImportHotjarSurvey();
    const resumeImportStore = useResumeImportStore();
    const {invokeHandlerForCtaNext} = resumeImportStore;
    const {isFunnelCtaNextEnabled} = storeToRefs(resumeImportStore);
    const {
        currentStepName,
        currentStepComponent,
        currentStepErrorComponent,
        funnelOrderedSteps,
        stepperCurrentIndex,
        stepperStepsCount,
        hasStepError,
        hasStepNext,
        goToStepNext,
        funnelReset,
    } = useFunnelSteps();

    const isFunnelVisible = ref(false);
    const isFunnelCtaCancelLoading = ref<boolean>(false);
    const isFunnelCtaNextLoading = ref<boolean>(false);
    const hasError = ref(false);
    const entryPoint = ref<EditionOpenedEntryPoint>(EditionOpenedEntryPoint.PROFILE_PAGE);
    const {isDisplayMobile} = useDisplayMobile('Xs');

    const ctaNextLabel = computed(() => (hasStepNext.value ? t('generic.navigation.button.next') : t('generic.navigation.button.finish')));
    const titleWidth = computed(() => (isDisplayMobile.value ? '100%' : '50%'));
    const title = computed(() =>
        currentStepName.value === 'EditResumeImportStepUploadFile' ? t('profile.experience.funnel.step.upload.title.v2') : undefined,
    );
    const subtitle = computed(() =>
        currentStepName.value === 'EditResumeImportStepUploadFile' ? t('profile.experience.funnel.step.upload.subtitle.v2') : undefined,
    );

    onMounted(() => {
        eventBus.on('experiences:resume-import-mode', handleExperiencesResumeImportMode);
        eventBus.on('experiences:cancel-import-hotjar-survey', triggerExperienceHotjarSurveyEvent);
    });

    onBeforeUnmount(() => {
        funnelReset();
        eventBus.off('experiences:resume-import-mode', handleExperiencesResumeImportMode);
        eventBus.off('experiences:cancel-import-hotjar-survey', triggerExperienceHotjarSurveyEvent);
    });

    function funnelClose() {
        isFunnelVisible.value = false;
        hasError.value = false;
        funnelReset();
    }
    function funnelOpen() {
        isFunnelVisible.value = true;
    }

    function handleExperiencesResumeImportMode(event: {entryPoint: EditionOpenedEntryPoint}) {
        entryPoint.value = event.entryPoint;
        funnelOpen();
    }

    function resetEntryPoint() {
        entryPoint.value = EditionOpenedEntryPoint.PROFILE_PAGE;
    }

    function handleClickOnFunnelCancel(): void {
        funnelClose();
        eventBus.emit('experiences:cancel-import-hotjar-survey');
    }

    async function handleClickOnFunnelNext(skipHandler: boolean): Promise<void> {
        isFunnelCtaCancelLoading.value = true;
        isFunnelCtaNextLoading.value = true;
        try {
            if (!skipHandler) {
                await invokeHandlerForCtaNext();
            }

            if (hasStepNext.value) {
                goToStepNext();
            } else {
                eventBus.emit('experiences:updated');
                funnelClose();
            }
        } catch (error) {
            if (error instanceof ResumeImportErrorWithRedirection && hasStepError.value) {
                hasError.value = true;
            } else {
                pushVJoySnackbar({
                    props: {
                        message: t('profile.creation.common.notification.error.technical'),
                        level: 'error',
                    },
                });
            }
        } finally {
            isFunnelCtaCancelLoading.value = false;
            isFunnelCtaNextLoading.value = false;
        }
    }

    function handleFunnelStepRetry(): void {
        funnelReset();
        hasError.value = false;
    }
    async function handleFunnelStepRetryManualMode(): Promise<void> {
        funnelClose();
        await navigateTo({path: '/profile/experiences/new', query: {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE}});
    }

    function handleFunnelStepSkip(): void {
        handleClickOnFunnelNext(true);
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .profile-edition-resume-import {
        &__title-wrapper {
            @include text.getFontBySize(secondary-600);

            @include mq.screen_xs() {
                @include text.getFontBySize(secondary-400);
            }
        }
        &__title {
            width: v-bind(titleWidth);
        }
    }
</style>
